import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/useData';
import { supabase } from '../lib/supabase';
import { Card, FormRow, Input, Btn, Avatar, PageHeader, Toast, Spinner } from '../components/UI';

export default function ProfileSettings() {
  const { profile, updatePassword, refreshOrg } = useAuth();
  const { toast, show } = useToast();

  const [form, setForm]           = useState({ name: '', email: '', phone: '' });
  const [saving, setSaving]       = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const [pwForm, setPwForm]   = useState({ newPassword: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({ name: profile.name || '', email: profile.email || '', phone: profile.phone || '' });
    }
  }, [profile]);

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim()) { show('Name is required.', 'error'); return; }
    setSaving(true);
    try {
      await supabase.from('profiles').update({ name: form.name.trim(), phone: form.phone.trim() }).eq('id', profile.id);
      if (form.email.trim() !== profile.email) {
        await supabase.auth.updateUser({ email: form.email.trim() });
        setEmailSent(true);
      }
      await refreshOrg();
      show('Profile updated!');
    } catch (err) { show(err.message || 'Failed to save.', 'error'); }
    finally { setSaving(false); }
  }

  async function handlePwSave(e) {
    e.preventDefault();
    if (pwForm.newPassword.length < 8) { show('Password must be at least 8 characters.', 'error'); return; }
    if (pwForm.newPassword !== pwForm.confirm) { show('Passwords do not match.', 'error'); return; }
    setPwSaving(true);
    try {
      await updatePassword(pwForm.newPassword);
      setPwForm({ newPassword: '', confirm: '' });
      show('Password updated!');
    } catch (err) { show(err.message || 'Failed to update password.', 'error'); }
    finally { setPwSaving(false); }
  }

  if (!profile) return null;

  const roleLabel = profile.role === 'owner' ? 'Owner' : profile.role === 'manager' ? 'Manager' : 'Employee';

  return (
    <div className="page">
      <Toast toast={toast} />
      <PageHeader title="Profile settings" subtitle="Manage your personal information and account security." />

      {/* Personal info */}
      <Card style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">Personal info</div></div>
        <form onSubmit={handleSave}>
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap' }}>
            <Avatar name={form.name || profile.name} color={profile.avatar_color} textColor={profile.avatar_text_color} size="lg" />
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 2 }}>{roleLabel} · {profile.org_id ? '' : ''}</div>
            </div>
          </div>

          <FormRow label="Full name">
            <Input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Your name"
              required
            />
          </FormRow>
          <FormRow label="Email" hint="A confirmation link will be sent to your new email address.">
            <Input
              type="email"
              value={form.email}
              onChange={e => { setForm(f => ({ ...f, email: e.target.value })); setEmailSent(false); }}
              placeholder="you@example.com"
              required
            />
          </FormRow>
          <FormRow label="Phone">
            <Input
              type="tel"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="+1 (555) 000-0000"
            />
          </FormRow>

          {emailSent && (
            <div style={{
              background: 'var(--amber-light, #fff8e1)', border: '1px solid var(--amber, #f59e0b)',
              borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: 13,
              color: 'var(--amber-dark, #92400e)', marginBottom: 16,
            }}>
              Confirmation sent to <strong>{form.email}</strong> — click the link in that email to confirm the change.
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Btn type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner /> : 'Save changes'}
            </Btn>
          </div>
        </form>
      </Card>

      {/* Work info (read-only) */}
      <Card style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">Work info</div></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 160px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Position</div>
              <div style={{ fontSize: 14 }}>{profile.position || '—'}</div>
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Role</div>
              <div style={{ fontSize: 14 }}>{roleLabel}</div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            Contact your manager to update your position or department.
          </div>
        </div>
      </Card>

      {/* Change password */}
      <Card>
        <div className="card-header"><div className="card-title">Change password</div></div>
        <form onSubmit={handlePwSave}>
          <FormRow label="New password" hint="At least 8 characters.">
            <Input
              type="password"
              value={pwForm.newPassword}
              onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))}
              placeholder="New password"
              autoComplete="new-password"
            />
          </FormRow>
          <FormRow label="Confirm password">
            <Input
              type="password"
              value={pwForm.confirm}
              onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
              placeholder="Repeat new password"
              autoComplete="new-password"
            />
          </FormRow>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Btn type="submit" variant="primary" disabled={pwSaving || !pwForm.newPassword}>
              {pwSaving ? <Spinner /> : 'Update password'}
            </Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}
